import { AgentRunStatusValues } from '@lucidtech/las-sdk-browser';
import { memo, useCallback, useMemo } from 'react';
import { MenuTrigger } from 'react-aria-components';

import { Button, Menu, MenuItem, Popover, Text, Tooltip, TooltipTrigger } from '@/components/core';
import { CheckmarkIcon, FilterIcon } from '@/components/icons';
import { STATUS_TO_NAME } from '@/constants';
import { StringColumnFilter, useInboxStore } from '@/store';

export type InboxFilterProps = object;

// eslint-disable-next-line no-empty-pattern
const IInboxFilter = ({}: InboxFilterProps) => {
  const addFilter = useInboxStore((state) => state.addFilter);
  const updateFilter = useInboxStore((state) => state.updateFilter);
  const deleteFilter = useInboxStore((state) => state.deleteFilter);

  const filters = useInboxStore((state) => state.filters);
  const statusFilter = useMemo(
    () => filters.find((f) => f.id === 'status') as StringColumnFilter | undefined,
    [filters]
  );

  const updateStatusFilter = useCallback(
    (value: string) => {
      let newValues;
      if (statusFilter?.value?.find((v) => v.id === value)) {
        newValues = statusFilter?.value?.filter((v) => v.id !== value) ?? [];
      } else {
        newValues = [...(statusFilter?.value ?? []), { id: value, name: STATUS_TO_NAME[value] }];
      }
      if (newValues.length === 0) {
        deleteFilter('status');
      } else {
        if (statusFilter) {
          updateFilter('status', { value: newValues });
        } else {
          addFilter({ id: 'status', type: 'string', condition: 'is', value: newValues });
        }
      }
    },
    [addFilter, deleteFilter, statusFilter, updateFilter]
  );

  return (
    <MenuTrigger>
      <TooltipTrigger>
        <Button className="hover:bg-gray-100" variant="plain">
          <FilterIcon className={statusFilter ? '!text-primary-500' : '!text-gray-400'} />
        </Button>

        <Tooltip variant="primary">
          <Text>Filter by status</Text>
        </Tooltip>
      </TooltipTrigger>
      <Popover>
        <MenuTrigger>
          <Menu>
            {AgentRunStatusValues.filter((v) => !['archived', 'running', 'review-in-progress'].includes(v)).map((s) => (
              <MenuItem key={s} onAction={() => updateStatusFilter(s)}>
                {statusFilter?.value?.find((v) => v.id === s) ? (
                  <div className="bg-primary-600 flex size-4 items-center justify-center rounded-xs">
                    <CheckmarkIcon className="text-white" />
                  </div>
                ) : (
                  <div className="flex size-4 items-center justify-center rounded-xs border border-gray-200" />
                )}
                <Text>{STATUS_TO_NAME[s] ?? s}</Text>
              </MenuItem>
            ))}
          </Menu>
        </MenuTrigger>
      </Popover>
    </MenuTrigger>
  );
};

export const InboxFilter = memo(IInboxFilter) as typeof IInboxFilter;
